import { useEffect, useRef } from 'react';

/**
 * Retro outrun sun with horizontal stripes
 */
const OutrunSun = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let animationId: number;
    let sunY = 0;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const horizonY = height * 0.55;
      const sunRadius = Math.min(width, height) * 0.15;

      // Animate sun bobbing
      sunY = horizonY - sunRadius * 0.3 + Math.sin(Date.now() / 2000) * 10;

      // Draw sun circle with gradient
      const sunGradient = ctx.createRadialGradient(
        centerX,
        sunY,
        sunRadius * 0.3,
        centerX,
        sunY,
        sunRadius
      );
      sunGradient.addColorStop(0, 'rgba(255, 40, 145, 0.9)'); // Hot pink center
      sunGradient.addColorStop(0.5, 'rgba(255, 120, 80, 0.6)'); // Orange mid
      sunGradient.addColorStop(1, 'rgba(139, 0, 139, 0.3)'); // Purple edge

      ctx.fillStyle = sunGradient;
      ctx.beginPath();
      ctx.arc(centerX, sunY, sunRadius, 0, Math.PI * 2);
      ctx.fill();

      // Draw horizontal stripes across sun
      ctx.strokeStyle = 'rgba(139, 0, 139, 0.8)'; // Purple
      ctx.lineWidth = 3;

      const stripeCount = 12;
      const stripeSpacing = (sunRadius * 2) / stripeCount;

      for (let i = 0; i < stripeCount; i++) {
        const y = sunY - sunRadius + i * stripeSpacing;
        const distFromCenter = Math.abs(y - sunY) / sunRadius;
        const halfWidth = sunRadius * Math.sqrt(1 - distFromCenter ** 2);

        if (halfWidth > 0) {
          ctx.beginPath();
          ctx.moveTo(centerX - halfWidth, y);
          ctx.lineTo(centerX + halfWidth, y);
          ctx.stroke();
        }
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default OutrunSun;
